import React, { useContext, useEffect, useState } from 'react';
import styles from './AdminSubmissionsTable.module.css';
import { AuthContext } from '../../../contexts/AuthContext';

const goalLabels = {
    edit: 'Редактирование',
    sole: 'Индивидуальная работа',
    group: 'Групповая работа',
};

const AdminSubmissionsTable = () => {
    const { token } = useContext(AuthContext);
    const [submissions, setSubmissions] = useState([]);
    const [search, setSearch] = useState('');
    const [commentDrafts, setCommentDrafts] = useState({});

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error('Ошибка при загрузке заявок');
                const data = await res.json();
                setSubmissions(data);
                setCommentDrafts(Object.fromEntries(data.map((s) => [s.id, s.adminComment || ''])));
            } catch (err) {
                console.error(err.message);
            }
        };
        fetchSubmissions();
    }, [token]);

    const handleDelete = async (id) => {
        if (!window.confirm('Вы действительно хотите удалить заявку?')) return;

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Ошибка при удалении');
            }
            setSubmissions((prev) => prev.filter((s) => s.id !== id));
        } catch (err) {
            alert(`Ошибка: ${err.message}`);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!res.ok) throw new Error('Ошибка при изменении статуса');

            const updated = await res.json();
            setSubmissions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
            setCommentDrafts((prev) => ({
                ...prev,
                [updated.id]: updated.adminComment || '',
            }));
        } catch (err) {
            alert(err.message || 'Ошибка при изменении статуса');
        }
    };

    const handleCommentChange = (id, value) => {
        setCommentDrafts((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const handleCommentSave = async (id) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/submissions/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ adminComment: commentDrafts[id] || '' }),
            });

            if (!res.ok) throw new Error('Ошибка при сохранении комментария');

            const updated = await res.json();
            setSubmissions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
            setCommentDrafts((prev) => ({
                ...prev,
                [updated.id]: updated.adminComment || '',
            }));
        } catch (err) {
            alert(err.message || 'Ошибка при сохранении комментария');
        }
    };

    const filtered = submissions.filter((s) =>
        `${s.goal} ${s.price} ${s.status} ${s.authorData?.fullName || ''} ${s.adminComment || ''}`
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    const buildFileUrl = (fileUrl) => {
        if (!fileUrl) return '';
        if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) return fileUrl;

        const apiBase = process.env.REACT_APP_API_URL || '';
        const baseNoApi = apiBase.replace(/\/api\/?$/i, '');
        let path = fileUrl;

        if (path.startsWith('undefined')) {
            path = path.replace(/^undefined\/?/, '/');
        }

        if (!path.startsWith('/')) path = `/${path}`;

        if (path.startsWith('/api/')) {
            if (!baseNoApi) return path;
            const base = baseNoApi.endsWith('/') ? baseNoApi.slice(0, -1) : baseNoApi;
            return `${base}${path}`;
        }

        if (!apiBase) return path;
        const base = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        return `${base}${path}`;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Заявки</h2>
                <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Поиск по автору/цели/цене/статусу..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.thead}>
                        <tr>
                            <th className={styles.th}>ID</th>
                            <th className={styles.th}>Автор</th>
                            <th className={styles.th}>Цель</th>
                            <th className={styles.th}>Цена ($)</th>
                            <th className={styles.th}>Печатная копия</th>
                            <th className={styles.th}>Статус</th>
                            <th className={styles.th}>Дата</th>
                            <th className={styles.th}>Файл</th>
                            <th className={styles.th}>Комментарий</th>
                            <th className={styles.th}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length > 0 ? (
                            filtered.map((s) => (
                                <tr key={s.id} className={styles.tr}>
                                    <td className={styles.td}>{s.id}</td>
                                    <td className={styles.td}>{s.authorData?.fullName || '-'}</td>
                                    <td className={styles.td}>{goalLabels[s.goal] || s.goal}</td>
                                    <td className={styles.td}>${s.price}</td>
                                    <td className={styles.td}>{s.printedCopy ? 'Да' : 'Нет'}</td>
                                    <td className={styles.td}>
                                        <select
                                            className={styles.statusSelect}
                                            value={s.status}
                                            onChange={(e) => handleStatusChange(s.id, e.target.value)}
                                        >
                                            <option value="pending">Ожидает</option>
                                            <option value="approved">Одобрено</option>
                                            <option value="inactive">Неактивно</option>
                                            <option value="rejected">Отклонено</option>
                                        </select>
                                    </td>
                                    <td className={styles.td}>{new Date(s.createdAt).toLocaleDateString()}</td>
                                    <td className={styles.td}>
                                        {s.fileUrl ? (
                                            <a
                                                href={buildFileUrl(s.fileUrl)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.fileLink}
                                            >
                                                Открыть
                                            </a>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className={styles.td}>
                                        <div className={styles.commentBox}>
                                            <textarea
                                                className={styles.commentInput}
                                                value={commentDrafts[s.id] ?? (s.adminComment || '')}
                                                onChange={(e) => handleCommentChange(s.id, e.target.value)}
                                                rows={3}
                                                placeholder="Комментарий..."
                                            />
                                            <button
                                                className={`${styles.button} ${styles.saveButton}`}
                                                onClick={() => handleCommentSave(s.id)}
                                            >
                                                Сохранить
                                            </button>
                                        </div>
                                    </td>
                                    <td className={styles.td}>
                                        <button
                                            className={`${styles.button} ${styles.deleteButton}`}
                                            onClick={() => handleDelete(s.id)}
                                        >
                                            Удалить
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className={styles.td} colSpan="10">
                                    Ничего не найдено
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminSubmissionsTable;
